<template>
  <div class="row g-2 mb-3">
    <div class="col-12">
      <div class="card no-hover pt-2 bg-blurry">
        <div class="table-responsive-md">
          <table class="table table-hover align-middle position-relative blur mb-0" itemscope itemtype="https://schema.org/Table">
            <thead>
            <tr>
              <th scope="col" class="small text-uppercase opacity-75 fw-normal pt-0">Reg.</th>
              <th class="small text-uppercase opacity-75 fw-normal pt-0">Model</th>
              <th class="small text-uppercase opacity-75 fw-normal pt-0">{{ isDutch ? 'Leeftijd' : 'Age'}}</th>
              <th class="small text-uppercase opacity-75 fw-normal pt-0">{{ isDutch ? 'Lst. vlucht' : 'Last Flight'}}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="aircraft in aircrafts.slice(0, limitAircrafts)"
                @click="openLink((isDutch ? '/nl/vliegtuigen/' : '/aircrafts/') + aircraft.slug)"
                :key="aircraft.registration">
              <th>
                <NuxtLink
                    class="badge rounded-pill small border mb-0 bg-blurry inherit"
                    :title="airline.name + ' ' + aircraft.registration + ' ' + aircraft.model"
                    :to="{ name: isDutch ? 'nl-vliegtuigen-slug' : 'aircrafts-slug', params: { slug: aircraft.slug } }">
                  {{ aircraft.registration }}
                </NuxtLink>
              </th>
              <th>
                {{ aircraft.model }}
              </th>
              <th>
                {{ formatAge(aircraft.age) }}
                <span>{{ isDutch ? 'jaar' : 'yr' }}</span>
              </th>
              <th>
                {{ formatLatestFlight(aircraft.latest_flight) }}
              </th>
            </tr>
            </tbody>
            <tfoot class="blur" v-if="!authStore.user && limitAircrafts < aircrafts.length">
              <tr v-for="n in 4">
                <th>
                  <span class="badge rounded-pill border mb-0 bg-blurry">ABC-DEFG</span>
                </th>
                <th>
                  Boeing 787-123
                </th>
                <th>
                  10
                </th>
                <th>
                  2021-12-31
                </th>
              </tr>
            </tfoot>
          </table>

          <div v-if="!authStore.user && limitAircrafts < aircrafts.length" class="table-disclaimer">
            <div class="bg-transparent text-center">
              <NuxtImg
                  class="mb-2"
                  alt="Pilot Emoji"
                  width="30"
                  height="30"
                  loading="lazy"
                  src="img/pilot-emoji.png"
              />
              <p v-if="isDutch" class="h3 text-primary">Meer zien? Meld je aan!</p>
              <p v-else class="h3 text-primary">Get Full Access</p>

              <p v-if="isDutch" class="mb-0">Voor volledige toegang, maak een <b>gratis account</b> of <b>log in</b>.</p>
              <p v-else class="mb-0">For full access, create a <b>free account</b> or <b>log in</b>.</p>

              <p v-if="isDutch" class="mb-0">Klik op <b>Account</b> in het menu om in te loggen.</p>
              <p v-else class="mb-0">Tap <b>Account</b> in the menu to get started.</p>
            </div>
          </div>

          <div class="pt-3 pb-3 ps-3" v-if="airline.children.length">
            <p v-if="isDutch" class="mb-0 mt-0 small opacity-75">
              Tabel laat ook vliegtuigen zien van
              <NuxtLink
                  v-for="airline in airline.children"
                  :to="{ name: 'nl-luchtvaartmaatschappijen-slug', params: { slug: airline.slug }}"
                  class="inherit fw-bold"
                  :title="airline.name">{{ airline.name }}</NuxtLink>.
            </p>
            <p v-else class="mb-0 mt-1 small opacity-75">
              The table also displays aircraft from <NuxtLink
                v-for="airline in airline.children"
                :to="{ name: 'airlines-slug', params: { slug: airline.slug }}"
                class="inherit fw-bold"
                :title="airline.name">{{ airline.name }}</NuxtLink>.
            </p>
          </div>

          <div class="text-center mb-3 pt-3 border-top" v-if="authStore.user && limitAircrafts < aircrafts.length">
            <button @click="increaseLimit" role="button" v-if="isDutch" class="badge bg-primary-darken border rounded-pill">Meer vliegtuigen laden</button>
            <button @click="increaseLimit" role="button" v-else class="badge bg-primary-darken border rounded-pill">Load more</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import moment from "moment";
import {useAuthStore} from "~/store/auth.js";

interface Aircraft {
  model: string,
  registration: string,
  age: string | null,
  latest_flight: number | null,
  slug: string
}

interface Props {
  aircrafts: Aircraft[],
  airline: object
}

const authStore = useAuthStore();
const isDutch = inject('isDutch');

const props = defineProps<Props>();
props.aircrafts.sort((a, b) => {
  if (a.latest_flight === null) return 1;
  if (b.latest_flight === null) return -1;
  return new Date(b.latest_flight) - new Date(a.latest_flight);
});

const limitAircrafts = ref(7);

const increaseLimit = () => {
  limitAircrafts.value += 25;
};

function formatLatestFlight(latestFlight: number | null) {
  if (latestFlight === null) {
    return null;
  }

  const latestFlightDate = moment(latestFlight * 1000);
  const today = moment().startOf('day');
  const yesterday = moment().subtract(1, 'days').startOf('day');

  if (latestFlightDate.isSame(today, 'day')) {
    return isDutch ? 'Vandaag' : 'Today';
  } else if (latestFlightDate.isSame(yesterday, 'day')) {
    return isDutch ? 'Gisteren' : 'Yesterday';
  } else {
    return latestFlightDate.format('D/M/Y');
  }
}

function formatAge(age: string | null) {
  if (age === null) {
    return '-';
  }

  const ageDate = moment(age);
  const now = moment();

  return now.diff(ageDate, 'years');
}

function openLink(url: string | URL | undefined) {
  setTimeout(() => {
    window.open(url, '_self');
  })
}
</script>